import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  CalendarPlus,
  CheckCheck,
  CheckCircle2,
  FileText,
  Eye,
  Gauge,
  Inbox,
  ListOrdered,
  PauseCircle,
  ShieldCheck,
  Timer,
  Truck,
  Wrench,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardChartTooltip from "../components/dashboard/DashboardChartTooltip";
import DashboardKpiCard from "../components/dashboard/DashboardKpiCard";
import DashboardPanel from "../components/dashboard/DashboardPanel";
import { CannibalDeviceOverview, ServiceRevenueOverview } from "../components/dashboard/DashboardEnhancementSections";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { RefreshButton } from "../components/ui/refresh-button";
import { Card, CardContent } from "../components/ui/card";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  DASHBOARD_BRANCHES,
  DASHBOARD_PERIODS,
  getDashboardOverview,
} from "../lib/dashboardApi";
import { formatApiError } from "../lib/api";

const REASON_LABELS = {
  queue_over_four_calendar_days: "Queue > 4 Days",
  pending_over_one_calendar_month: "Pending > 1 Month",
  awaiting_validation: "Awaiting Validation",
  in_delivery: "In Delivery",
};

const REASON_ORDER = Object.keys(REASON_LABELS);
const STAGE_ORDER = ["incoming", "queue", "pending", "validation", "delivery", "done"];
const STAGE_LABELS = {
  incoming: "Incoming",
  queue: "Queue",
  pending: "Pending",
  validation: "Validation",
  delivery: "Delivery",
  done: "Done",
};
const STAGE_PRESENTATIONS = {
  incoming: { icon: Inbox, card: "border-info/25 bg-info/5", iconTone: "border-info/30 bg-info/10 text-info" },
  queue: { icon: ListOrdered, card: "border-primary/25 bg-primary/5", iconTone: "border-primary/30 bg-primary/10 text-primary" },
  pending: { icon: Timer, card: "border-warning/25 bg-warning/5", iconTone: "border-warning/30 bg-warning/10 text-warning" },
  validation: { icon: ShieldCheck, card: "border-movement/25 bg-movement/5", iconTone: "border-movement/30 bg-movement/10 text-movement" },
  delivery: { icon: Truck, card: "border-category/25 bg-category/5", iconTone: "border-category/30 bg-category/10 text-category" },
  done: { icon: CheckCheck, card: "border-success/25 bg-success/5", iconTone: "border-success/30 bg-success/10 text-success" },
};
const WARRANTY_ORDER = [
  { status: "doa", label: "DOA", color: "var(--neon-cyan-base)" },
  { status: "in_warranty", label: "IW", color: "var(--neon-mint-base)" },
  { status: "out_of_warranty", label: "OOW", color: "var(--neon-pink-base)" },
  { status: "void", label: "Void", color: "var(--neon-yellow-base)" },
];
const BRANCH_ORDER = ["jakarta", "surabaya", "bali"];
const BRANCH_LABELS = { jakarta: "Jakarta", surabaya: "Surabaya", bali: "Bali" };
const CHART_COLORS = {
  cyan: "var(--neon-cyan-base)",
  mint: "var(--neon-mint-base)",
  yellow: "var(--neon-yellow-base)",
  purple: "var(--neon-purple-base)",
  pink: "var(--neon-pink-base)",
  grid: "hsl(var(--border))",
  text: "hsl(var(--muted-foreground))",
};

const DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Jakarta",
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatRelevantDate(item) {
  const value = item?.reason === "queue_over_four_calendar_days"
    ? item?.date_in
    : item?.stage_entered_at;
  if (!value) return "–";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "–" : DATE_FORMATTER.format(parsed);
}

function numeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasPositiveValue(rows, keys) {
  return rows.some((row) => keys.some((key) => numeric(row?.[key]) > 0));
}

function DashboardControls({ period, branch, onPeriodChange, onBranchChange, onRefresh, refreshing, mobile = false }) {
  return (
    <div className={mobile ? "flex w-full min-w-0 flex-nowrap items-end gap-2" : "flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end lg:w-auto lg:justify-end"} data-testid={mobile ? "mobile-dashboard-controls" : "dashboard-controls"}>
      <div className={mobile ? "min-w-0 flex-1" : "min-w-0 flex-1 sm:w-44 sm:flex-none"}>
        <label htmlFor="dashboard-period" className={mobile ? "sr-only" : "mb-1.5 block text-xs font-semibold text-muted-foreground"}>
          Period
        </label>
        <Select value={period} onValueChange={onPeriodChange} disabled={refreshing}>
          <SelectTrigger id="dashboard-period" aria-label="Period" className={`rounded-[var(--action-radius-normal)] ${mobile ? "h-10 min-w-0 w-full px-2 text-xs" : ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DASHBOARD_PERIODS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className={mobile ? "min-w-0 flex-1" : "min-w-0 flex-1 sm:w-44 sm:flex-none"}>
        <label htmlFor="dashboard-branch" className={mobile ? "sr-only" : "mb-1.5 block text-xs font-semibold text-muted-foreground"}>
          Branch
        </label>
        <Select value={branch} onValueChange={onBranchChange} disabled={refreshing}>
          <SelectTrigger id="dashboard-branch" aria-label="Branch" className={`rounded-[var(--action-radius-normal)] ${mobile ? "h-10 min-w-0 w-full px-2 text-xs" : ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DASHBOARD_BRANCHES.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <RefreshButton
        onClick={onRefresh}
        loading={refreshing}
        iconOnly={mobile}
        accessibleLabel={refreshing ? "Refreshing dashboard" : "Refresh dashboard"}
        data-action-category="retry-refresh"
        data-testid="dashboard-refresh"
      />
    </div>
  );
}

function DashboardLoading() {
  return (
    <div className="space-y-6" data-testid="dashboard-loading" aria-label="Loading Dashboard">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <Card key={index} className="rounded-lg">
            <CardContent className="p-5">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="mt-3 h-9 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {Array.from({ length: 8 }, (_, index) => (
          <Card key={index} className="rounded-lg">
            <CardContent className="space-y-4 p-5">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-[240px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EmptyChartMessage({ children }) {
  return (
    <div className="pointer-events-none absolute inset-x-4 top-1/2 z-10 -translate-y-1/2 rounded-md border border-dashed border-border bg-card/90 px-3 py-2 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function FollowUpSection({ followUp, onViewTicket }) {
  const items = Array.isArray(followUp?.items) ? followUp.items : [];
  const reasonCounts = REASON_ORDER.reduce((counts, reason) => {
    counts[reason] = items.filter((item) => item?.reason === reason).length;
    return counts;
  }, {});

  return (
    <DashboardPanel
      title="Tickets Requiring Follow-up"
      description="Current Service Tickets matching the approved follow-up conditions"
      testid="dashboard-follow-up"
      className="xl:col-span-2"
    >
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {REASON_ORDER.map((reason) => (
          <div key={reason} className="min-w-0 rounded-lg border border-border bg-muted/30 p-3">
            <p className="break-words text-xs font-medium text-muted-foreground">{REASON_LABELS[reason]}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{reasonCounts[reason]}</p>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{numeric(followUp?.total).toLocaleString()} matching tickets</span>
        {followUp?.truncated ? <span>Showing the first {items.length} returned tickets</span> : null}
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          No tickets match the current follow-up conditions.
        </div>
      ) : (
        <div className="max-w-full overflow-x-auto rounded-lg border border-border">
          <table className="wms-table min-w-[900px]" data-testid="dashboard-follow-up-table">
            <thead>
              <tr>
                <th>Ticket Number</th>
                <th>Branch</th>
                <th>Stage</th>
                <th>Device Model</th>
                <th>Reason</th>
                <th>Relevant Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="max-w-[190px] break-all font-medium" title={item.ticket_number}>{item.ticket_number}</td>
                  <td>{BRANCH_LABELS[item.branch] || item.branch || "–"}</td>
                  <td>{STAGE_LABELS[item.stage] || item.stage || "–"}</td>
                  <td className="max-w-[240px] break-words" title={item.device_model}>{item.device_model || "–"}</td>
                  <td className="max-w-[220px] break-words">{REASON_LABELS[item.reason] || item.reason || "–"}</td>
                  <td className="whitespace-nowrap tabular-nums">{formatRelevantDate(item)}</td>
                  <td className="whitespace-nowrap">
                    <Button type="button" variant="info" size="row-icon" title="View ticket" aria-label={`View ticket ${item.ticket_number}`} onClick={() => onViewTicket(item)}>
                      <Eye />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardPanel>
  );
}

function metricComparison(metric, type) {
  if (!metric || metric.comparison === "unavailable") return "Previous-period data unavailable";
  if (metric.comparison === "unchanged") return "No change";
  if (type === "lead-time") {
    const magnitude = numeric(metric.change_days).toFixed(1);
    return metric.comparison === "improved"
      ? `${magnitude} days better than previous period`
      : `${magnitude} days higher than previous period`;
  }
  const magnitude = Math.abs(numeric(metric.change_percentage_points)).toFixed(1);
  return metric.comparison === "improved"
    ? `${magnitude} percentage points higher than previous period`
    : `${magnitude} percentage points lower than previous period`;
}

function OperationalMetricCard({ label, value, supportingText, comparison, testid, icon: Icon, iconTone }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted/20 p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <p className="break-words text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</p>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${iconTone}`} aria-hidden="true">
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 break-words text-3xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="mt-2 break-words text-sm text-muted-foreground">{supportingText}</p>
      <p className="mt-1 break-words text-xs font-medium text-foreground">{comparison}</p>
    </div>
  );
}

function StageOverview({ rows, metrics }) {
  const byStage = new Map((Array.isArray(rows) ? rows : []).map((row) => [row.stage, row]));
  const leadTime = metrics?.average_lead_time || {};
  const ratio = metrics?.completion_to_intake_ratio || {};
  return (
    <DashboardPanel
      title="Service Stage Overview"
      description="Incoming–Delivery show active tickets; Done shows completions in the selected period"
      testid="dashboard-stage-overview"
      className="h-full"
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {STAGE_ORDER.map((stage) => {
          const presentation = STAGE_PRESENTATIONS[stage];
          const StageIcon = presentation.icon;
          return (
            <div key={stage} className={`min-w-0 rounded-lg border p-3 ${presentation.card}`} data-testid={`dashboard-stage-${stage}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="break-words text-xs font-medium text-muted-foreground">{STAGE_LABELS[stage]}</p>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${presentation.iconTone}`} aria-hidden="true">
                  <StageIcon className="h-3.5 w-3.5" />
                </span>
              </div>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{numeric(byStage.get(stage)?.count)}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 2xl:grid-cols-2">
        <OperationalMetricCard
          label="Average Lead Time"
          value={leadTime.value_days == null ? "â€“" : `${numeric(leadTime.value_days).toFixed(1)} days`}
          supportingText={`Based on ${numeric(leadTime.sample_count).toLocaleString()} completed tickets`}
          comparison={metricComparison(leadTime, "lead-time")}
          testid="dashboard-average-lead-time"
          icon={Timer}
          iconTone="border-info/30 bg-info/10 text-info"
        />
        <OperationalMetricCard
          label="Completion-to-Intake Ratio"
          value={ratio.percentage == null ? "â€“" : `${numeric(ratio.percentage).toFixed(1)}%`}
          supportingText={`${numeric(ratio.completed_count).toLocaleString()} completed / ${numeric(ratio.intake_count).toLocaleString()} received`}
          comparison={metricComparison(ratio, "ratio")}
          testid="dashboard-completion-intake-ratio"
          icon={Gauge}
          iconTone="border-movement/30 bg-movement/10 text-movement"
        />
      </div>
    </DashboardPanel>
  );
}

function CreatedCompletedChart({ rows }) {
  const data = Array.isArray(rows) ? rows : [];
  const empty = !hasPositiveValue(data, ["created", "completed"]);
  return (
    <DashboardPanel title="Created vs Completed" description="Latest five Jakarta calendar weeks" testid="dashboard-created-completed" className="h-full">
      <div className="relative h-[300px] min-w-0" role="img" aria-label="Created and completed Service Tickets for five weeks">
        {empty ? <EmptyChartMessage>No created or completed tickets in these weeks.</EmptyChartMessage> : null}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 8, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
            <YAxis allowDecimals={false} tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} />
            <RechartsTooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.35)" }} />
            <Bar dataKey="created" name="Created" fill={CHART_COLORS.cyan} radius={[3, 3, 0, 0]} />
            <Bar dataKey="completed" name="Completed" fill={CHART_COLORS.mint} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm bg-[var(--neon-cyan-base)]" />Created</span>
        <span><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm bg-[var(--neon-mint-base)]" />Completed</span>
      </div>
    </DashboardPanel>
  );
}

function WarrantyChart({ distribution }) {
  const incoming = Array.isArray(distribution?.items) ? distribution.items : [];
  const data = WARRANTY_ORDER.map((definition) => {
    const row = incoming.find((item) => item.status === definition.status) || {};
    return { ...definition, count: numeric(row.count), percentage: numeric(row.percentage) };
  });
  const total = numeric(distribution?.total);
  return (
    <DashboardPanel title="Warranty Status Distribution" description={`${total.toLocaleString()} tickets represented`} testid="dashboard-warranty">
      <div className="grid min-w-0 grid-cols-1 items-center gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
        <div className="relative h-[260px] min-w-0" role="img" aria-label="Warranty status donut chart">
          {total === 0 ? <EmptyChartMessage>No warranty tickets in this period.</EmptyChartMessage> : null}
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="label" innerRadius="55%" outerRadius="80%" paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                {data.map((item) => <Cell key={item.status} fill={item.color} />)}
              </Pie>
              <RechartsTooltip content={<DashboardChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-2xl font-semibold tabular-nums text-foreground">{total}</p>
              <p className="text-[11px] text-muted-foreground">Tickets</p>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {data.map((item) => (
            <div key={item.status} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-xs">
              <span className="flex items-center gap-2 font-medium">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden="true" />
                {item.label}
              </span>
              <span className="text-right tabular-nums text-muted-foreground">
                {item.count} · {item.percentage.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </DashboardPanel>
  );
}

function HorizontalBarPanel({ title, description, rows, valueKey, emptyMessage, testid, showSummary = true }) {
  const data = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, [valueKey]: numeric(row[valueKey]) }));
  const empty = data.length === 0 || !hasPositiveValue(data, [valueKey]);
  return (
    <DashboardPanel title={title} description={description} testid={testid}>
      <div className="relative h-[300px] min-w-0" role="img" aria-label={title}>
        {empty ? <EmptyChartMessage>{emptyMessage}</EmptyChartMessage> : null}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart layout="vertical" data={data} margin={{ top: 8, right: 20, left: 16, bottom: 4 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
            <YAxis type="category" dataKey="display_label" width={130} tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(value) => value.length > 21 ? `${value.slice(0, 20)}…` : value} />
            <RechartsTooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.35)" }} />
            <Bar dataKey={valueKey} name={valueKey === "count" ? "Tickets" : "Quantity"} fill={CHART_COLORS.purple} radius={[0, 3, 3, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {showSummary && data.length ? (
        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          {data.map((row) => <p key={row.item_id || row.model} className="break-words">{row.display_label}: <span className="font-semibold tabular-nums text-foreground">{row[valueKey]}</span></p>)}
        </div>
      ) : null}
    </DashboardPanel>
  );
}

function IntakeTrendChart({ rows, branch }) {
  const data = Array.isArray(rows) ? rows : [];
  const visibleBranches = branch === "all" ? BRANCH_ORDER : [branch];
  const empty = !hasPositiveValue(data, visibleBranches);
  const colors = { jakarta: CHART_COLORS.cyan, surabaya: CHART_COLORS.mint, bali: CHART_COLORS.yellow };
  return (
    <DashboardPanel title="Service Intake Trend by Branch" description="Latest six Jakarta calendar months" testid="dashboard-intake-trend" className="xl:col-span-2">
      <div className="relative h-[320px] min-w-0" role="img" aria-label="Service intake trend for Jakarta, Surabaya, and Bali">
        {empty ? <EmptyChartMessage>No Service intake in these months.</EmptyChartMessage> : null}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 12, right: 16, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
            <YAxis allowDecimals={false} tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} />
            <RechartsTooltip content={<DashboardChartTooltip />} />
            {visibleBranches.map((series, index) => (
              <Line
                key={series}
                type="monotone"
                dataKey={series}
                name={BRANCH_LABELS[series]}
                stroke={colors[series]}
                strokeWidth={2.5}
                strokeDasharray={index === 1 ? "6 3" : index === 2 ? "2 3" : undefined}
                dot={{ r: 3, fill: "hsl(var(--card))", strokeWidth: 2 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
        {visibleBranches.map((series) => <span key={series}>{BRANCH_LABELS[series]}</span>)}
      </div>
    </DashboardPanel>
  );
}

function PartsByBranchChart({ rows }) {
  const incoming = Array.isArray(rows) ? rows : [];
  const data = BRANCH_ORDER.map((branch) => {
    const row = incoming.find((item) => item.branch === branch) || {};
    return { branch, label: BRANCH_LABELS[branch], quantity: numeric(row.quantity) };
  });
  const empty = !hasPositiveValue(data, ["quantity"]);
  return (
    <DashboardPanel title="Parts Used by Branch" description="Summed Usage quantity for the selected period" testid="dashboard-parts-branch">
      <div className="relative h-[300px] min-w-0" role="img" aria-label="Parts used by Branch">
        {empty ? <EmptyChartMessage>No parts used in this period.</EmptyChartMessage> : null}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 8, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
            <YAxis allowDecimals={false} tick={{ fill: CHART_COLORS.text, fontSize: 11 }} tickLine={false} axisLine={false} />
            <RechartsTooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.35)" }} />
            <Bar dataKey="quantity" name="Quantity" fill={CHART_COLORS.mint} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardPanel>
  );
}

export default function Dashboard({ mobile = false }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("this_month");
  const [branch, setBranch] = useState("all");
  const [data, setData] = useState(null);
  const [initialError, setInitialError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestSequence = useRef(0);
  const dataRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++requestSequence.current;
    const hasExistingData = Boolean(dataRef.current);
    setInitialError("");
    setRefreshError("");
    setRefreshing(hasExistingData);

    getDashboardOverview({ period, branch, signal: controller.signal })
      .then((nextData) => {
        if (requestId !== requestSequence.current) return;
        dataRef.current = nextData;
        setData(nextData);
      })
      .catch((error) => {
        if (controller.signal.aborted || requestId !== requestSequence.current) return;
        const message = formatApiError(error);
        if (dataRef.current) setRefreshError(message);
        else setInitialError(message);
      })
      .finally(() => {
        if (requestId === requestSequence.current) setRefreshing(false);
      });

    return () => controller.abort();
  }, [branch, period, refreshKey]);

  const modelRows = useMemo(
    () => (Array.isArray(data?.top_device_models) ? data.top_device_models : []).map((row) => ({
      ...row,
      display_label: row.model || "–",
    })),
    [data]
  );
  const partRows = useMemo(
    () => (Array.isArray(data?.top_used_parts) ? data.top_used_parts : []).map((row) => ({
      ...row,
      display_label: [row.part_number, row.part_name].filter(Boolean).join(" · ") || "–",
    })),
    [data]
  );

  const header = (
    <OrdinaryPageHeader
      title="Operational Dashboard"
      actions={<DashboardControls
        period={period}
        branch={branch}
        onPeriodChange={setPeriod}
        onBranchChange={setBranch}
        onRefresh={() => setRefreshKey((value) => value + 1)}
        refreshing={refreshing}
        mobile={mobile}
      />}
      data-testid="dashboard-page-header"
    />
  );

  if (!data && initialError) {
    return (
      <OrdinaryPageBody data-testid="dashboard">
        {header}
        <Alert className="border-warning/40 bg-warning/10" data-testid="dashboard-initial-error">
          <AlertTitle>Dashboard data could not be loaded</AlertTitle>
          <AlertDescription className="mt-2 flex flex-col items-start gap-3">
            <span>{initialError}</span>
            <Button type="button" variant="retry" onClick={() => setRefreshKey((value) => value + 1)} data-action-category="retry-refresh">
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </OrdinaryPageBody>
    );
  }

  if (!data) {
    return (
      <OrdinaryPageBody data-testid="dashboard">
        {header}
        <DashboardLoading />
      </OrdinaryPageBody>
    );
  }

  const kpis = data.kpis || {};
  const viewTicket = (ticket) => {
    const returnUrl = encodeURIComponent("/");
    navigate(`/service/tickets/${encodeURIComponent(ticket.id)}?return=${returnUrl}`);
  };
  return (
    <OrdinaryPageBody data-testid="dashboard">
      {header}
      <div className="min-w-0 space-y-5 overflow-x-hidden" data-testid="dashboard-specialized-body">

      {refreshError ? (
        <Alert className="border-warning/40 bg-warning/10" data-testid="dashboard-refresh-error">
          <AlertTitle>Dashboard refresh was not completed</AlertTitle>
          <AlertDescription>{refreshError}. The previous Dashboard data remains displayed.</AlertDescription>
        </Alert>
      ) : null}

      <div className={`grid grid-cols-1 gap-3 ${mobile ? "min-[420px]:grid-cols-2" : "sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6"}`} data-testid="dashboard-kpis">
        <DashboardKpiCard label="Active Service Tickets" value={numeric(kpis.active_service_tickets).toLocaleString()} icon={Activity} accent="border-primary/30 bg-primary/10 text-primary" testid="kpi-active-tickets" />
        <DashboardKpiCard label="Tickets Created This Month" value={numeric(kpis.tickets_created_this_month).toLocaleString()} icon={CalendarPlus} accent="border-primary/30 bg-primary/10 text-primary" testid="kpi-created-month" />
        <DashboardKpiCard label="Completed This Month" value={numeric(kpis.completed_this_month).toLocaleString()} icon={CheckCircle2} accent="border-success/30 bg-success/10 text-success" testid="kpi-completed-month" />
        <DashboardKpiCard label="Parts Used This Month" value={numeric(kpis.parts_used_this_month).toLocaleString()} icon={Wrench} accent="border-success/30 bg-success/10 text-success" testid="kpi-parts-month" />
        <DashboardKpiCard label="Active Holds" value={numeric(kpis.active_holds).toLocaleString()} icon={PauseCircle} accent="border-warning/30 bg-warning/10 text-warning" testid="kpi-active-holds" />
        <DashboardKpiCard
          label="Quotations Created"
          value={numeric(kpis.quotations_created).toLocaleString()}
          icon={FileText}
          accent="border-[var(--neon-purple-base)] bg-[var(--neon-purple-dark-tint)] text-[var(--neon-purple-outline-foreground)]"
          testid="kpi-quotations"
        />
      </div>

      <div className={`grid min-w-0 grid-cols-1 gap-5 ${mobile ? "" : "xl:grid-cols-2"}`}>
        <IntakeTrendChart rows={data.service_intake_trend} branch={branch} />
        <StageOverview rows={data.stage_overview} metrics={data.operational_metrics} />
        <ServiceRevenueOverview overview={data.service_revenue_overview} branch={branch} />
        <CreatedCompletedChart rows={data.created_vs_completed} />
        <CannibalDeviceOverview overview={data.cannibal_overview} branch={branch} />
        <WarrantyChart distribution={data.warranty_distribution} />
        <HorizontalBarPanel
          title="Top 5 Service Models"
          description="Service Ticket intake for the selected period"
          rows={modelRows}
          valueKey="count"
          emptyMessage="No Service models in this period."
          testid="dashboard-top-models"
          showSummary={false}
        />
        <PartsByBranchChart rows={data.parts_used_by_branch} />
        <HorizontalBarPanel
          title="Top 5 Used Parts"
          description="Active and legacy-active Usage quantity for the selected period"
          rows={partRows}
          valueKey="quantity"
          emptyMessage="No parts used in this period."
          testid="dashboard-top-parts"
          showSummary={false}
        />
        {!mobile ? <FollowUpSection followUp={data.follow_up} onViewTicket={viewTicket} /> : null}
      </div>
      </div>
    </OrdinaryPageBody>
  );
}
